import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiUserPlus,
  FiMessageCircle,
  FiSend,
  FiLoader,
  FiAlertCircle,
} from "react-icons/fi";
import { useRoom } from "../../../context/RoomContext";
import "./JoinRequestModal.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.15 },
  },
};

const JoinRequestModal = ({ room, onClose }) => {
  const { joinRoom, loading } = useRoom();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!message.trim()) {
      setError("Please provide a message explaining why you'd like to join");
      return;
    }

    try {
      await joinRoom(room._id, message);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to send join request");
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="jrm-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleOverlayClick}
      >
        <motion.div
          className="jrm-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="jrm-header">
            <div className="jrm-header-content">
              <div className="jrm-header-icon">
                <FiUserPlus />
              </div>
              <div className="jrm-header-text">
                <h2 className="jrm-title">Request to Join</h2>
                <p className="jrm-subtitle">
                  Send a join request to the lab leader
                </p>
              </div>
            </div>
            <motion.button
              className="jrm-close-btn"
              onClick={onClose}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </motion.button>
          </div>

          {/* Body */}
          <div className="jrm-body">
            {/* Room Info */}
            <div className="jrm-room-info">
              <h3 className="jrm-room-name">{room.name}</h3>
              <p className="jrm-room-field">
                {room.field}
                {room.subField ? ` · ${room.subField}` : ""}
              </p>
            </div>

            {/* Instructions */}
            <p className="jrm-instructions">
              Introduce yourself and explain why you'd like to join this
              research lab. The lab leader will review your request.
            </p>

            <form onSubmit={handleSubmit} className="jrm-form">
              {/* Message Input */}
              <div className="jrm-form-group">
                <label className="jrm-label">
                  <FiMessageCircle className="jrm-label-icon" />
                  <span>Your Message</span>
                  <span className="jrm-required">*</span>
                </label>
                <textarea
                  className={`jrm-textarea ${
                    error ? "jrm-textarea-error" : ""
                  }`}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder="I would like to join this lab because..."
                  rows={5}
                  disabled={loading}
                />
                <div className="jrm-field-footer">
                  <span className="jrm-char-count">
                    {message.length} characters
                  </span>
                </div>
              </div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    className="jrm-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <FiAlertCircle />
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="jrm-actions">
                <motion.button
                  type="button"
                  className="jrm-btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  className="jrm-btn-primary"
                  disabled={loading || !message.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        style={{ display: "inline-flex" }}
                      >
                        <FiLoader />
                      </motion.span>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FiSend />
                      <span>Send Request</span>
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JoinRequestModal;
